import { useEffect, useMemo, useRef, useState } from 'react'
import {
  getSpeechRecognitionConstructor,
  type BrowserSpeechRecognition,
} from './speech-recognition'
import {
  normalizeMediaRecoveryError,
  type MediaRecoveryState,
} from '../media-recovery'

export function useSpeechTranscriber({
  continuous = true,
  interimResults = true,
  lang,
}: {
  readonly continuous?: boolean
  readonly interimResults?: boolean
  readonly lang: string
}) {
  const recognitionConstructor = useMemo(() => getSpeechRecognitionConstructor(), [])
  const recognitionRef = useRef<BrowserSpeechRecognition | null>(null)
  const [recovery, setRecovery] = useState<MediaRecoveryState | null>(null)
  const [isListening, setIsListening] = useState(false)
  const [transcript, setTranscript] = useState('')

  const stopListening = () => {
    recognitionRef.current?.stop()
  }

  const resetTranscript = () => {
    setTranscript('')
  }

  const startListening = () => {
    if (!recognitionConstructor) {
      setRecovery(normalizeMediaRecoveryError('not supported', 'SPEECH_RECOGNITION'))
      return false
    }

    setRecovery(null)
    setTranscript('')

    const recognition = new recognitionConstructor()
    recognition.continuous = continuous
    recognition.interimResults = interimResults
    recognition.lang = lang
    recognition.onresult = (event) => {
      const nextTranscript = Array.from(event.results)
        .map((result) => result[0]?.transcript ?? '')
        .join(' ')
        .trim()

      setTranscript(nextTranscript)
    }
    recognition.onerror = (event) => {
      setRecovery(normalizeMediaRecoveryError(event.error, 'SPEECH_RECOGNITION'))
      setIsListening(false)
    }
    recognition.onend = () => {
      setIsListening(false)
    }

    try {
      recognitionRef.current = recognition
      recognition.start()
      setIsListening(true)
    } catch (error) {
      setRecovery(normalizeMediaRecoveryError(error, 'SPEECH_RECOGNITION'))
      setIsListening(false)
      return false
    }

    return true
  }

  useEffect(
    () => () => {
      stopListening()
    },
    [],
  )

  return {
    errorCode: recovery?.code ?? null,
    errorMessage: recovery?.message ?? null,
    isListening,
    isSupported: Boolean(recognitionConstructor),
    resetTranscript,
    startListening,
    stopListening,
    transcript,
  }
}
